import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  Mic,
  FileText,
  Type,
  Brain,
  Sparkles,
  CheckCircle,
  BarChart3,
  FileCheck,
  TrendingUp,
  ClipboardList,
  Heart,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface OnboardingTutorialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  healthAreaName?: string;
}

const SCENES = [
  {
    id: "capture",
    title: "Capture de qualquer forma",
    subtitle: "4 métodos para você escolher",
    description: "Fotografe prontuários, grave áudios clínicos, suba arquivos ou digite diretamente.",
  },
  {
    id: "ai-process",
    title: "IA processa automaticamente",
    subtitle: "Inteligência artificial trabalhando",
    description: "Nossa IA extrai, valida e estrutura todos os dados clínicos em segundos.",
  },
  {
    id: "structured",
    title: "Dados organizados",
    subtitle: "Informações prontas para uso",
    description: "Resultados de exames, métricas de saúde e evoluções organizados automaticamente.",
  },
  {
    id: "apps",
    title: "Use nos aplicativos",
    subtitle: "Potencialize seu atendimento",
    description: "Acompanhe evoluções, gere relatórios, tome decisões baseadas em dados.",
  },
];

export function OnboardingTutorialModal({
  open,
  onOpenChange,
  healthAreaName,
}: OnboardingTutorialModalProps) {
  const [currentScene, setCurrentScene] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance scenes
  useEffect(() => {
    if (!open || !isAutoPlaying) return;

    const timer = setInterval(() => {
      setCurrentScene((prev) => (prev + 1) % SCENES.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [open, isAutoPlaying]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setCurrentScene(0);
      setIsAutoPlaying(true);
    }
  }, [open]);

  const goNext = () => {
    setIsAutoPlaying(false);
    setCurrentScene((prev) => Math.min(prev + 1, SCENES.length - 1));
  };

  const goPrev = () => {
    setIsAutoPlaying(false);
    setCurrentScene((prev) => Math.max(prev - 1, 0));
  };

  const scene = SCENES[currentScene];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
        <DialogHeader className="sr-only">
          <DialogTitle>Como funciona o envio de dados</DialogTitle>
        </DialogHeader>

        {/* Close button */}
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 z-50 rounded-full p-1.5 bg-background/80 hover:bg-muted transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-6 pb-4">
          {/* Header */}
          <div className="text-center mb-6">
            <motion.div
              key={scene.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="space-y-1"
            >
              <p className="text-xs font-medium text-primary uppercase tracking-wider">
                {scene.subtitle}
              </p>
              <h2 className="text-2xl font-bold">{scene.title}</h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {scene.description}
              </p>
            </motion.div>
          </div>

          {/* Animation Area */}
          <div className="relative h-[280px] bg-gradient-to-br from-muted/30 to-muted/10 rounded-2xl border border-border/50 overflow-hidden">
            <AnimatePresence mode="wait">
              {currentScene === 0 && <CaptureScene key="capture" />}
              {currentScene === 1 && <AIProcessScene key="ai" />}
              {currentScene === 2 && <StructuredScene key="structured" />}
              {currentScene === 3 && <AppsScene key="apps" healthAreaName={healthAreaName} />}
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={goPrev}
              disabled={currentScene === 0}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>

            {/* Progress dots */}
            <div className="flex gap-2">
              {SCENES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentScene(idx);
                  }}
                  className={cn(
                    "h-2 rounded-full transition-all duration-300",
                    idx === currentScene
                      ? "w-6 bg-primary"
                      : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                  )}
                />
              ))}
            </div>

            {currentScene < SCENES.length - 1 ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={goNext}
                className="gap-1"
              >
                Próximo
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => onOpenChange(false)}
                className="gap-1"
              >
                Entendi!
                <CheckCircle className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Scene 1: Capture Methods
function CaptureScene() {
  const methods = [
    { icon: Camera, label: "Câmera", color: "text-teal-500", bg: "bg-teal-500/10", delay: 0 },
    { icon: Mic, label: "Áudio", color: "text-emerald-500", bg: "bg-emerald-500/10", delay: 0.15 },
    { icon: FileText, label: "Arquivo", color: "text-blue-500", bg: "bg-blue-500/10", delay: 0.3 },
    { icon: Type, label: "Texto", color: "text-purple-500", bg: "bg-purple-500/10", delay: 0.45 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex items-center justify-center p-8"
    >
      <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
        {methods.map((method, idx) => (
          <motion.div
            key={method.label}
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: method.delay, duration: 0.4 }}
            className="relative"
          >
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: idx * 0.5 }}
              className={cn(
                "flex flex-col items-center justify-center gap-2 p-4 rounded-xl border border-border/50",
                method.bg
              )}
            >
              <method.icon className={cn("h-8 w-8", method.color)} />
              <span className="text-sm font-medium">{method.label}</span>
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-full bg-primary/20"
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 1, 0],
            x: [0, (i % 2 === 0 ? 1 : -1) * 30],
            y: [-20, -60],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.3,
          }}
          style={{
            left: `${20 + i * 12}%`,
            top: "70%",
          }}
        />
      ))}
    </motion.div>
  );
}

// Scene 2: AI Processing
function AIProcessScene() {
  const dataItems = [
    "Glicemia: 95 mg/dL",
    "Hemoglobina: 14.2 g/dL",
    "Peso: 72 kg",
    "Pressão: 120/80",
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex items-center justify-center"
    >
      {/* Central Brain */}
      <div className="relative">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 rounded-full border-2 border-dashed border-primary/30"
          style={{ width: 120, height: 120, top: -10, left: -10 }}
        />
        
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center relative z-10"
        >
          <Brain className="h-12 w-12 text-primary" />
        </motion.div>

        {/* Pulse rings */}
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute inset-0 rounded-2xl border border-primary/30"
            initial={{ scale: 1, opacity: 0.5 }}
            animate={{ scale: [1, 1.8], opacity: [0.5, 0] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.6,
            }}
          />
        ))}
      </div>

      {/* Data items appearing */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 space-y-2">
        {dataItems.map((item, idx) => (
          <motion.div
            key={item}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 + idx * 0.3 }}
            className="flex items-center gap-2 text-xs bg-background/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-border/50"
          >
            <Sparkles className="h-3 w-3 text-primary" />
            <span>{item}</span>
          </motion.div>
        ))}
      </div>

      {/* Input items */}
      <div className="absolute left-8 top-1/2 -translate-y-1/2 space-y-3">
        {[FileText, Camera, Mic].map((Icon, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0.3 }}
            animate={{ opacity: [0.3, 1, 0.3], x: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: idx * 0.4 }}
            className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center"
          >
            <Icon className="h-5 w-5 text-muted-foreground" />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// Scene 3: Structured Data
function StructuredScene() {
  const results = [
    { label: "Glicemia", value: "95", unit: "mg/dL", status: "normal" },
    { label: "Colesterol", value: "180", unit: "mg/dL", status: "normal" },
    { label: "IMC", value: "24.5", unit: "kg/m²", status: "normal" },
    { label: "Hemoglobina", value: "14.2", unit: "g/dL", status: "normal" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex items-center justify-center p-6"
    >
      <div className="w-full max-w-md">
        {/* Mini table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-background/90 backdrop-blur-sm rounded-xl border border-border/50 overflow-hidden"
        >
          <div className="grid grid-cols-4 gap-2 p-2 bg-muted/50 text-xs font-medium text-muted-foreground">
            <span>Parâmetro</span>
            <span className="text-center">Valor</span>
            <span className="text-center">Unidade</span>
            <span className="text-center">Status</span>
          </div>
          
          {results.map((result, idx) => (
            <motion.div
              key={result.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + idx * 0.15 }}
              className="grid grid-cols-4 gap-2 p-2 text-sm border-t border-border/30"
            >
              <span className="font-medium">{result.label}</span>
              <span className="text-center">{result.value}</span>
              <span className="text-center text-muted-foreground">{result.unit}</span>
              <span className="text-center">
                <span className="inline-flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="h-3 w-3" />
                </span>
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Success indicator */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1 }}
          className="flex items-center justify-center gap-2 mt-4 text-sm text-green-600"
        >
          <FileCheck className="h-4 w-4" />
          <span>4 registros estruturados</span>
        </motion.div>
      </div>
    </motion.div>
  );
}

// Scene 4: Apps in Action
function AppsScene({ healthAreaName }: { healthAreaName?: string }) {
  const apps = [
    { icon: TrendingUp, label: "Evolução", color: "text-blue-500", bg: "bg-blue-500/10" },
    { icon: BarChart3, label: "Relatórios", color: "text-purple-500", bg: "bg-purple-500/10" },
    { icon: ClipboardList, label: "Protocolos", color: "text-teal-500", bg: "bg-teal-500/10" },
    { icon: Heart, label: "Saúde", color: "text-rose-500", bg: "bg-rose-500/10" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex flex-col items-center justify-center p-6"
    >
      {/* Health area badge */}
      {healthAreaName && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium"
        >
          Área: {healthAreaName}
        </motion.div>
      )}

      <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
        {apps.map((app, idx) => (
          <motion.div
            key={app.label}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: idx * 0.1 }}
            whileHover={{ scale: 1.05 }}
            className={cn(
              "flex flex-col items-center justify-center gap-2 p-4 rounded-xl border border-border/50 cursor-pointer transition-shadow hover:shadow-lg",
              app.bg
            )}
          >
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: idx * 0.2 }}
            >
              <app.icon className={cn("h-6 w-6", app.color)} />
            </motion.div>
            <span className="text-xs font-medium">{app.label}</span>
          </motion.div>
        ))}
      </div>

      {/* Connection lines animation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-4 flex items-center gap-2 text-xs text-muted-foreground"
      >
        <Sparkles className="h-3 w-3 text-primary" />
        <span>Todos conectados aos seus dados</span>
      </motion.div>
    </motion.div>
  );
}

